/**
 * Shared "how do we draw this Digital Workforce agent as a small avatar"
 * logic — used by the sidebar's Digital Team strip and the global chat bar
 * so the same agent renders identically (same color, same initials) in
 * both places, which is the whole point of "a different icon shows what
 * you're speaking with."
 */
import type { DwAgentRecord } from '../features/claudeagents/claudeAgentApi';
import type { ActiveAgentInfo } from '../context/ActiveAgentContext';
import { agentAvatarFor } from './agentPhotos';

/** Small agent icons (sidebar strip, chat bar, chat messages, Command Center)
 *  sit on one neutral tile: initials or photo identify the agent, not a hue.
 *  The Digital Workforce page keeps its own per-agent colors (avatarColor in
 *  its agent meta) and doesn't use this. Pair with a neutral border and
 *  text-primary initials in the avatar's CSS. */
export const AGENT_AVATAR_NEUTRAL_BG = 'var(--agent-avatar-tile)';

export function colorForAgent(_id: string): string {
  return AGENT_AVATAR_NEUTRAL_BG;
}

export function initialsForAgent(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
}

/** Default agents are stored as "Name-{userId}" — strips that suffix. */
export function displayNameForAgent(name: string, isSystem?: boolean): string {
  return isSystem ? name.replace(/-\d+$/, '') : name;
}

/** A Digital Workforce agent record -> what the chat surfaces need to draw
 *  and address it (name, avatar color/initials, built-in agent photo).
 *  Shared by the global chat bar and the new-chat agent home so the same
 *  agent looks the same in both. */
export function toActiveAgentInfo(r: DwAgentRecord): ActiveAgentInfo {
  const name = displayNameForAgent(r.name, r.is_system);
  return {
    id: r.agent_id,
    name,
    avatarColor: colorForAgent(r.agent_id),
    avatarInitials: initialsForAgent(name),
    avatarPhoto: agentAvatarFor(r.agent_id, r.is_system ? name : undefined),
  };
}
